import React, { useEffect } from "react"
import clsx from "clsx"
import { useSelector, useDispatch } from "react-redux"
import { Link, useNavigate } from "react-router-dom"
import {
    AppBar,
    Toolbar,
    Typography,
    Drawer,
    List,
    IconButton,
    ListItem,
    ListItemText,
    ListItemIcon,
    Divider,
    Tooltip,
    Box,
    Chip,
} from "@mui/material"
import MenuIcon from "@mui/icons-material/Menu"
import Brightness3Icon from "@mui/icons-material/Brightness3"
import WbSunnyIcon from "@mui/icons-material/WbSunny"
import HomeIcon from "@mui/icons-material/Home"
import LogoutIcon from "@mui/icons-material/Logout"
import SettingsIcon from "@mui/icons-material/Settings"
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople"
import DescriptionIcon from "@mui/icons-material/Description"
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"
import NewReleasesIcon from "@mui/icons-material/NewReleases"
import CottageIcon from "@mui/icons-material/Cottage"

import logo from "../../images/precipartLogoSquare.png"
import useStyles, { AppBarShiftStyles, AppBarStyles, ApplicationNameStyles, chipStyles, ContentShiftStyles, ContentStyles, DrawerHeaderStyles, DrawerStyles, LogoStyles } from "./styles/NavigationStyles"
import { homeURL, environment } from "../../config"
import {
    getUserData,
    toggleDarkMode,
    logoutUser
} from "../../actions/authActions"
import {
    updateHostList,
    updateVisitorList,
    updateConfRoomList
} from "../../actions/visitorFormActions"
import resetHeaders from "../../functions/resetHeaders"
import firebaseAuth from "../../auth/firebaseAuth"

function Navigation() {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const auth = useSelector((state) => state.auth)
    const userData = useSelector((state) => state.auth.userData)
    const permission = useSelector((state) => state.auth.permissions)
    const [open, setOpen] = React.useState(false)

    const navigationItems = [
        {
            text: "Home Page",
            path: "/home",
            visible: true,
            icon: <CottageIcon />
        },
        {
            text: "Admin Settings",
            path: "/admin",
            visible:
                permission.AdminVisitorAnnouncement ||
                permission.AdminISODocumentUpdate,
            icon: <SettingsIcon />
        },
        {
            text: "Visitor Announcements",
            path: "/visitor",
            visible: true,
            icon: <EmojiPeopleIcon />
        },
        {
            text: "ISO Regulatory Updates",
            path: "/iso",
            visible: true,
            icon: <DescriptionIcon />
        }
    ]

    const handleDarkModeToggle = async () => {
        await dispatch(toggleDarkMode(auth.userData))
        await dispatch(getUserData(auth.userData.GoogleID))
    }

    const handleDrawerOpen = () => {
        setOpen(true)
    }

    const handleDrawerClose = () => {
        setOpen(false)
    }

    const handleGoHome = async () => {
        setOpen(false)
        window.open(homeURL, "_self")
    }

    const handleHelp = async () => {
        setOpen(false)
        window.open(
            "https://precipart0.sharepoint.com/sites/PrecipartWebApplications/SitePages/Visitor.aspx",
            "_blank"
        )
    }

    const handleClick = async () => {
        // clear host list box area
        await dispatch(updateHostList([]))
        // clear visitor info list box area
        await dispatch(updateVisitorList([]))
        // clear conference room info list box area
        await dispatch(updateConfRoomList([]))

        handleDrawerClose()
    }

    const handleLogout = async () => {
        handleDrawerClose()
        dispatch(logoutUser())
        await firebaseAuth.signOut()
        navigate("/")
    }

    const handleKeyDown = async (e) => {
        if (e.key === "Escape") {
            setOpen(false)
        }
    }

    return (
        <div style={{ display: "flex" }}>
            <AppBar
                position="fixed"
                sx={[
                    AppBarStyles,
                    open && AppBarShiftStyles,
                ]}
                elevation={24}
                onKeyDown={handleKeyDown}
            >
                <Toolbar>
                    <Box
                        component="img"
                        alt="Precipart Logo"
                        src={logo}
                        sx={LogoStyles}
                    />
                    <Typography
                        variant="h6"
                        sx={ApplicationNameStyles}
                        noWrap
                    >
                        Notification Station{" "}
                        {environment && environment !== "production" && (
                            <Chip
                                sx={chipStyles}
                                icon={<NewReleasesIcon />}
                                label={environment.toUpperCase()}
                                style={{ marginLeft: "5px" }}
                            />
                        )}
                    </Typography>
                    {userData.AssociateOID && (
                        <Tooltip title={open ? "Close Menu" : "Open Menu"}>
                            <IconButton
                                color="inherit"
                                aria-label={
                                    open ? "close drawer" : "open drawer"
                                }
                                edge="end"
                                onClick={
                                    open ? handleDrawerClose : handleDrawerOpen
                                }
                            >
                                <MenuIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Toolbar>
            </AppBar>

            <Box
                sx={[
                    ContentStyles,
                    open && ContentShiftStyles,
                ]}
            >
                <Box sx={DrawerHeaderStyles} />
            </Box>
            <Drawer
                sx={DrawerStyles}
                variant="persistent"
                anchor="right"
                open={open}
                PaperProps={{ elevation: 15 }}
            >
                <Box sx={DrawerHeaderStyles}></Box>
                <Box sx={DrawerHeaderStyles}>
                    <Tooltip title="Return Home">
                        <IconButton onClick={handleGoHome} >
                            <HomeIcon/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Logout">
                        <IconButton onClick={handleLogout}>
                            <LogoutIcon/>
                        </IconButton>
                    </Tooltip>
                    {auth.userData && (
                        <Tooltip title="Toggle Darkmode">
                            <IconButton onClick={handleDarkModeToggle}>
                                {auth.userData.DarkMode === "dark" ? (
                                    <WbSunnyIcon />
                                ) : (
                                    <Brightness3Icon />
                                )}
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                <Divider />
                <List>
                    {navigationItems
                        .filter((item) => item.visible)
                        .map((item) => (
                            <ListItem
                                button
                                key={item.text}
                                component={Link}
                                to={item.path}
                                onClick={handleDrawerClose}
                                sx={{
                                    textDecoration: "none",
                                    color: "inherit",
                                }}
                            >
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.text} />
                            </ListItem>
                        ))}
                </List>
            </Drawer>
        </div>
    )
}

export default Navigation
